import React, { useState } from 'react';
import TaskChatManager from './TaskChatManager';
import {
  Box,
  Paper,
  Typography,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Chip,
  Button,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  Grid,
  Tabs,
  Tab,
  Card,
  CardContent,
  Divider
} from '@mui/material';
import {
  Assignment as TaskIcon,
  Add as AddIcon,
  PlayArrow as PlayArrowIcon,
  Pause as PauseIcon,
  Stop as StopIcon,
  Delete as DeleteIcon,
  Chat as ChatIcon,
  Description as TemplateIcon,
  Create as CreateIcon,
  Message as MessageIcon
} from '@mui/icons-material';

const AgentTasks = ({ agent, tasks, onTaskAction, onCreateTask, onRefreshTasks }) => {
  const [showCreateTask, setShowCreateTask] = useState(false);
  const [error, setError] = useState(null);
  const [selectedTab, setSelectedTab] = useState(0);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [newTask, setNewTask] = useState({
    name: '',
    description: '',
    category: '',
    frequency: 'daily',
    priority: 'medium'
  });
  
  // Task chat functionality
  const [showTaskChat, setShowTaskChat] = useState(false);
  const [selectedTaskForChat, setSelectedTaskForChat] = useState(null);

  // Task templates from agent definition
  const taskTemplates = agent?.taskTemplates || [
    {
      id: 'traffic_analysis',
      name: 'Traffic Pattern Analysis',
      description: 'Analyze traffic patterns and identify congestion hotspots',
      category: 'analysis',
      frequency: 'daily',
      priority: 'high',
      defaultSettings: {
        analysisType: 'pattern',
        timeRange: '24h',
        outputFormat: 'report'
      }
    },
    {
      id: 'safety_monitoring',
      name: 'Safety Incident Monitoring',
      description: 'Monitor and report safety incidents in real-time',
      category: 'monitoring',
      frequency: 'continuous',
      priority: 'high',
      defaultSettings: {
        alertThreshold: 'immediate',
        notificationType: 'email',
        escalationLevel: 'high'
      }
    },
    {
      id: 'performance_report',
      name: 'System Performance Report',
      description: 'Generate comprehensive system performance reports',
      category: 'reporting',
      frequency: 'weekly',
      priority: 'medium',
      defaultSettings: {
        reportType: 'comprehensive',
        includeMetrics: true,
        exportFormat: 'pdf'
      }
    },
    {
      id: 'data_cleanup',
      name: 'Data Cleanup and Maintenance',
      description: 'Perform routine data cleanup and system maintenance',
      category: 'automation',
      frequency: 'weekly',
      priority: 'low',
      defaultSettings: {
        cleanupType: 'routine',
        retentionPeriod: '30d',
        backupBeforeCleanup: true
      }
    }
  ];

  const getStatusColor = (status) => {
    switch (status) {
      case 'active': return 'success';
      case 'paused': return 'warning';
      case 'pending': return 'info';
      case 'completed': return 'success';
      case 'failed': return 'error';
      default: return 'default';
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'high': return 'error';
      case 'medium': return 'warning';
      case 'low': return 'success';
      default: return 'default';
    }
  };

  const handleCreateTask = async () => {
    try {
      let taskData = { ...newTask };
      
      // If using a template, merge with template data
      if (selectedTemplate) {
        taskData = {
          ...selectedTemplate,
          ...newTask,
          templateId: selectedTemplate.id,
          settings: selectedTemplate.defaultSettings
        };
      }
      
      const result = await onCreateTask(taskData);
      if (result.success) {
        setShowCreateTask(false);
        setSelectedTab(0);
        setSelectedTemplate(null);
        setNewTask({ name: '', description: '', category: '', frequency: 'daily', priority: 'medium' });
        setError(null);
      } else {
        setError(result.error || 'Failed to create task');
      }
    } catch (err) {
      setError('Failed to create task');
    }
  };

  const handleTemplateSelect = (template) => {
    setSelectedTemplate(template);
    setNewTask({
      name: template.name,
      description: template.description,
      category: template.category,
      frequency: template.frequency,
      priority: template.priority
    });
    setSelectedTab(1); // Switch to custom task tab
  };

  const handleCustomTask = () => {
    setSelectedTemplate(null);
    setNewTask({ name: '', description: '', category: '', frequency: 'daily', priority: 'medium' });
    setSelectedTab(1); // Switch to custom task tab
  };

  const handleTaskAction = async (taskId, action) => {
    try {
      await onTaskAction(taskId, action);
      setError(null);
    } catch (err) {
      setError(`Failed to ${action} task`);
    }
  };

  const handleTaskChat = (task) => {
    setSelectedTaskForChat(task);
    setShowTaskChat(true);
  };

  const handleCloseTaskChat = () => {
    setShowTaskChat(false);
    setSelectedTaskForChat(null);
  };

  return (
    <>
      <Paper sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Typography variant="h6">
            Agent Tasks ({tasks.length})
          </Typography>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setShowCreateTask(true)}
          >
            Create Task
          </Button>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {tasks.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <TaskIcon sx={{ fontSize: 48, mb: 2, opacity: 0.5 }} />
            <Typography variant="h6" gutterBottom>
              No tasks assigned
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Create a new task to get started with this agent.
            </Typography>
          </Box>
        ) : (
          <List>
            {tasks.map((task) => (
              <ListItem key={task._id} sx={{ border: 1, borderColor: 'divider', borderRadius: 1, mb: 1 }}>
                <ListItemIcon>
                  <TaskIcon />
                </ListItemIcon>
                <ListItemText
                  primary={task.name}
                  secondary={
                    <Box>
                      <Typography variant="body2" color="text.secondary">
                        {task.description}
                      </Typography>
                      <Box sx={{ mt: 1, display: 'flex', gap: 1 }}>
                        <Chip label={task.status} size="small" color={getStatusColor(task.status)} />
                        <Chip label={task.priority} size="small" color={getPriorityColor(task.priority)} />
                        <Chip label={task.frequency} size="small" />
                      </Box>
                    </Box>
                  }
                />
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Tooltip title="Chat about this task">
                    <IconButton size="small" onClick={() => handleTaskChat(task)}>
                      <ChatIcon />
                    </IconButton>
                  </Tooltip>
                  {task.status === 'active' && (
                    <Tooltip title="Pause task">
                      <IconButton size="small" onClick={() => handleTaskAction(task._id, 'pause')}>
                        <PauseIcon />
                      </IconButton>
                    </Tooltip>
                  )}
                  {task.status === 'paused' && (
                    <Tooltip title="Resume task">
                      <IconButton size="small" onClick={() => handleTaskAction(task._id, 'resume')}>
                        <PlayArrowIcon />
                      </IconButton>
                    </Tooltip>
                  )}
                  <Tooltip title="Execute task">
                    <IconButton size="small" onClick={() => handleTaskAction(task._id, 'execute')}>
                      <PlayArrowIcon />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Delete task">
                    <IconButton size="small" color="error" onClick={() => handleTaskAction(task._id, 'delete')}>
                      <DeleteIcon />
                    </IconButton>
                  </Tooltip>
                </Box>
              </ListItem>
            ))}
          </List>
        )}
      </Paper>

      {/* Create Task Dialog */}
      <Dialog 
        open={showCreateTask} 
        onClose={() => setShowCreateTask(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Create New Task</DialogTitle>
        <DialogContent>
          <Tabs value={selectedTab} onChange={(e, newValue) => setSelectedTab(newValue)} sx={{ mb: 3 }}>
            <Tab label="Task Templates" />
            <Tab label="Custom Task" />
          </Tabs>

          {selectedTab === 0 && (
            <Box>
              <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
                <TemplateIcon sx={{ mr: 1 }} />
                Available Task Templates
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                Choose from predefined task templates or create a custom task
              </Typography>
              
              <Grid container spacing={2}>
                {taskTemplates.map((template) => (
                  <Grid item xs={12} sm={6} key={template.id}>
                    <Card 
                      sx={{ 
                        cursor: 'pointer',
                        '&:hover': { boxShadow: 4, bgcolor: 'action.hover' },
                        border: selectedTemplate?.id === template.id ? 2 : 1,
                        borderColor: selectedTemplate?.id === template.id ? 'primary.main' : 'divider'
                      }}
                      onClick={() => handleTemplateSelect(template)}
                    >
                      <CardContent>
                        <Typography variant="h6" gutterBottom>
                          {template.name}
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                          {template.description}
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
                          <Chip label={template.category} size="small" />
                          <Chip label={template.frequency} size="small" />
                          <Chip 
                            label={template.priority} 
                            size="small" 
                            color={template.priority === 'high' ? 'error' : template.priority === 'medium' ? 'warning' : 'success'}
                          />
                        </Box>
                        <Button 
                          variant="outlined" 
                          size="small" 
                          fullWidth
                          onClick={(e) => {
                            e.stopPropagation();
                            handleTemplateSelect(template);
                          }}
                        >
                          Use This Template
                        </Button>
                      </CardContent>
                    </Card>
                  </Grid>
                ))}
              </Grid>
              
              <Divider sx={{ my: 3 }} />
              
              <Box sx={{ textAlign: 'center' }}>
                <Typography variant="body1" gutterBottom>
                  Don't see what you need?
                </Typography>
                <Button 
                  variant="contained" 
                  startIcon={<CreateIcon />}
                  onClick={handleCustomTask}
                >
                  Create Custom Task
                </Button>
              </Box>
            </Box>
          )}

          {selectedTab === 1 && (
            <Box>
              <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
                <CreateIcon sx={{ mr: 1 }} />
                {selectedTemplate ? `Customize: ${selectedTemplate.name}` : 'Create Custom Task'}
              </Typography>
              
              {selectedTemplate && (
                <Alert severity="info" sx={{ mb: 3 }}>
                  You're customizing the "{selectedTemplate.name}" template. You can modify any fields below.
                </Alert>
              )}
              
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Task Name"
                    value={newTask.name}
                    onChange={(e) => setNewTask({...newTask, name: e.target.value})}
                    required
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Description"
                    multiline
                    rows={3}
                    value={newTask.description}
                    onChange={(e) => setNewTask({...newTask, description: e.target.value})}
                    required
                  />
                </Grid>
                <Grid item xs={6}>
                  <FormControl fullWidth>
                    <InputLabel>Category</InputLabel>
                    <Select
                      value={newTask.category}
                      onChange={(e) => setNewTask({...newTask, category: e.target.value})}
                      label="Category"
                    >
                      <MenuItem value="analysis">Analysis</MenuItem>
                      <MenuItem value="monitoring">Monitoring</MenuItem>
                      <MenuItem value="reporting">Reporting</MenuItem>
                      <MenuItem value="automation">Automation</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={6}>
                  <FormControl fullWidth>
                    <InputLabel>Frequency</InputLabel>
                    <Select
                      value={newTask.frequency}
                      onChange={(e) => setNewTask({...newTask, frequency: e.target.value})}
                      label="Frequency"
                    >
                      <MenuItem value="once">Once</MenuItem>
                      <MenuItem value="daily">Daily</MenuItem>
                      <MenuItem value="weekly">Weekly</MenuItem>
                      <MenuItem value="monthly">Monthly</MenuItem>
                      <MenuItem value="continuous">Continuous</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12}>
                  <FormControl fullWidth>
                    <InputLabel>Priority</InputLabel>
                    <Select
                      value={newTask.priority}
                      onChange={(e) => setNewTask({...newTask, priority: e.target.value})}
                      label="Priority"
                    >
                      <MenuItem value="low">Low</MenuItem>
                      <MenuItem value="medium">Medium</MenuItem>
                      <MenuItem value="high">High</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
              </Grid>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            setShowCreateTask(false);
            setSelectedTab(0);
            setSelectedTemplate(null);
            setNewTask({ name: '', description: '', category: '', frequency: 'daily', priority: 'medium' });
          }}>
            Cancel
          </Button>
          <Button 
            onClick={handleCreateTask} 
            variant="contained"
            disabled={!newTask.name || !newTask.description}
          >
            {selectedTemplate ? 'Create from Template' : 'Create Task'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Task Chat Manager */}
      {showTaskChat && selectedTaskForChat && (
        <TaskChatManager
          task={selectedTaskForChat}
          agent={agent}
          appId={agent.appId}
          onClose={handleCloseTaskChat}
        />
      )}
    </>
  );
};

export default AgentTasks; 